import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarded, setOnboarded] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setOrg(response.data.org);
      
      // Check onboarding status
      const onboardingDone = localStorage.getItem('onboarding_complete');
      setOnboarded(!!onboardingDone);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { token, user: userData, org: orgData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      setOrg(orgData);
      
      // Check onboarding
      const onboardingDone = localStorage.getItem('onboarding_complete');
      setOnboarded(!!onboardingDone);
      
      toast.success('Welcome back!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const signup = async (name, email, password, orgName) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/signup`, {
        name,
        email,
        password,
        org_name: orgName
      });
      const { token, user: userData, org: orgData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
      setOrg(orgData);
      setOnboarded(false);
      
      toast.success('Account created!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
      return false;
    }
  };

  const demoLogin = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/demo`);
      const { token, user: userData, org: orgData } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('onboarding_complete', 'true'); // Demo users skip onboarding
      setUser(userData);
      setOrg(orgData);
      setOnboarded(true);

      toast.success('Welcome to the demo!');
      return true;
    } catch (error) {
      toast.error('Demo login failed');
      return false;
    }
  };

  // WorkOS AuthKit SSO - redirect to WorkOS for authentication
  const signInWithWorkOS = useCallback(async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.orgId) params.append('org_id', options.orgId);
      if (options.inviteCode) params.append('invite_code', options.inviteCode);

      const queryString = params.toString();
      const url = `${API_URL}/api/auth/workos/authorize${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);

      // Redirect to WorkOS AuthKit
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('WorkOS auth error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate login');
    }
  }, []);

  // Handle WorkOS OAuth callback - exchange code for JWT
  const handleWorkOSCallback = useCallback(async (code, state) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/workos/callback`, {
        code,
        state
      });

      const { token, user: userData, org: orgData, is_new_user: isNewUser, invite_code: inviteCode } = response.data;

      localStorage.setItem('token', token);
      setUser(userData);
      setOrg(orgData);

      if (isNewUser) {
        setOnboarded(false);
        toast.success('Account created!');
      } else {
        // Check onboarding status for existing users
        const onboardingDone = localStorage.getItem('onboarding_complete');
        setOnboarded(!!onboardingDone);
        toast.success('Welcome back!');
      }

      return { success: true, isNewUser, inviteCode };
    } catch (error) {
      console.error('WorkOS callback error:', error);
      const errorMessage = error.response?.data?.detail || 'Authentication failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('onboarding_complete');
    setUser(null);
    setOrg(null);
    setOnboarded(false);
    toast.success('Logged out');
  };

  const completeOnboarding = () => {
    localStorage.setItem('onboarding_complete', 'true');
    setOnboarded(true);
  };

  return (
    <AuthContext.Provider value={{
      user,
      org,
      loading,
      onboarded,
      // Legacy auth methods (kept for backward compatibility)
      login,
      signup,
      // WorkOS SSO methods
      signInWithWorkOS,
      handleWorkOSCallback,
      // Shared methods
      demoLogin,
      logout,
      completeOnboarding
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}